import { useCallback, useEffect, useState } from "react";
import { usePushNotifications } from "../../../../common/infrastructure/hooks/usePushNotifications";
import {
  getNotificationPermission,
  getNotificationsSectionStatus,
  isInAppBrowser,
  isIosSafariTab,
  isPushSupported,
} from "../../../../common/infrastructure/utilities/pushUtility";
import { clearNotificationPromptHistory } from "../../../../common/infrastructure/utilities/notificationPromptStorageUtility";
import {
  getErrorToast,
  getSuccessToast,
} from "../../../../common/infrastructure/utilities/toastUtility";
import { NotificationsSectionStatus, PushSubscribeResult } from "../../../../common/enums";

/**
 * The "Известия" tab in "Моят профил": shows whether notifications are on for this device
 * and lets the user turn them on or off. Covers people who said "Не сега", a new device,
 * and staff who never make reservations (so never see the reservation dialog).
 */
export const useNotificationsSection = () => {
  const { subscribe, unsubscribe, hasActiveSubscription } = usePushNotifications();
  const [status, setStatus] = useState<NotificationsSectionStatus | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const refreshStatus = useCallback(async () => {
    const hasSubscription = await hasActiveSubscription();
    setStatus(
      getNotificationsSectionStatus({
        supported: isPushSupported(),
        permission: getNotificationPermission(),
        iosSafariTab: isIosSafariTab(),
        inAppBrowser: isInAppBrowser(),
        hasSubscription,
      })
    );
  }, [hasActiveSubscription]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const onEnable = useCallback(async () => {
    setIsBusy(true);
    const result = await subscribe();
    setIsBusy(false);

    if (result === PushSubscribeResult.Subscribed) {
      clearNotificationPromptHistory();
      getSuccessToast("Известията са включени на това устройство.");
    } else if (result === PushSubscribeResult.Failed) {
      getErrorToast("Известията не успяха да се включат. Опитайте отново.");
    }

    await refreshStatus();
  }, [subscribe, refreshStatus]);

  const onDisable = useCallback(async () => {
    setIsBusy(true);
    try {
      await unsubscribe();
      getSuccessToast("Известията са изключени на това устройство.");
    } catch {
      getErrorToast("Известията не успяха да се изключат. Опитайте отново.");
    } finally {
      setIsBusy(false);
      await refreshStatus();
    }
  }, [unsubscribe, refreshStatus]);

  return { status, isBusy, onEnable, onDisable };
};
